import { type CSSProperties, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Pressable } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { useItemsVersion } from '../../utils/useItemsVersion';
import { useShallowStable } from '../../utils/useShallowStable';
import { Tooltip } from '../Tooltip';
import {
  DEFAULT_ANIMATION_DURATION_MS,
  pillTransitionStyle,
} from './SegmentedControl.animation';
import {
  segmentClassName,
  SegmentContent,
  segmentStateOf,
} from './SegmentedControl.parts';
import {
  type PillRect,
  pillRectFor,
  shouldSquarePill,
} from './SegmentedControl.progress';
import type { SegmentedControlProps } from './SegmentedControl.types';
import {
  activeSegmentVariants,
  segmentedControlVariants,
} from './SegmentedControl.variants';

/**
 * Cross-platform single-select segmented control — WEB.
 *
 * The active pill (fill/border/shadow) is a single absolute overlay measured to
 * the active segment's box and slid via a CSS transform+width transition — the
 * same "one measured overlay" model as the native mirror
 * (`SegmentedControl.native.tsx`), rather than a framer-motion `layoutId`, so
 * the shared package keeps its minimal dependency surface. Honours
 * `prefers-reduced-motion` (transition disabled).
 *
 * Mirrors the Figma "Flashka Design System" `SegmentedControl`
 * (file `YZK9r3wDhvw1pY60KlVEIj`; light 1528:1833 / dark 3172:14143).
 *
 * @example
 * ```tsx
 * import { SegmentedControl } from '@flashka/shared/ui';
 * import { List, Grid } from '@flashka/shared/ui/icons';
 *
 * const [view, setView] = useState<'list' | 'grid'>('list');
 * <SegmentedControl
 *   accessibilityLabel="View mode"
 *   value={view}
 *   onValueChange={setView}
 *   items={[
 *     { value: 'list', label: 'List', icon: List, keybinding: 'L' },
 *     { value: 'grid', label: 'Grid', icon: Grid, keybinding: 'G' },
 *   ]}
 * />
 * ```
 */
export function SegmentedControl<V extends string = string>({
  items,
  value,
  onValueChange,
  size = 'l',
  activeStyle = 'tertiary',
  fullWidth = false,
  disabled = false,
  // haptic is native-only; accepted for API parity (no-op on web).
  haptic: _haptic = 'selection',
  className,
  segmentClassName: segmentClassNameProp,
  accessibilityLabel,
  testID,
  segmentWebProps,
  segmentNativeProps,
  animationDuration,
  animationEasing,
  animationBezier,
  disableAnimation = false,
  // selectionProgress is native-only; accepted for API parity (no-op on web —
  // there is no UI-thread loop to read `.value` from, so honouring it would
  // mean a rAF loop plus a per-frame render, strictly worse than the CSS
  // transition already in place).
  selectionProgress: _selectionProgress,
  // native-only; accepted for API parity (no-op on web — the cells are plain
  // flex children here and resize with the row's own layout).
  animateSegmentResize: _animateSegmentResize,
}: SegmentedControlProps<V>) {
  const activeIndex = items.findIndex((i) => i.value === value);
  const segRefs = useRef<(HTMLElement | null)[]>([]);
  const [pill, setPill] = useState<PillRect | null>(null);

  // Measure the active segment's box (track-relative — the track is its
  // offsetParent). Re-measures when the active segment or layout changes; a
  // ResizeObserver on the track catches width changes (fullWidth, container
  // resize, late web-font metrics) that shift segment offsets. `setPill`
  // bails on an unchanged rect so a redundant measure (e.g. a ResizeObserver
  // tick) never triggers a render — and deps stay PRIMITIVE (not the `items`
  // array, whose identity changes every render) so the effect can't loop.
  const itemCount = items.length;
  // The item SET, as a primitive dep. `itemCount` alone cannot see a same-length
  // swap (Document|Notes|Mindmaps → Notes|Mindmaps|Quizzes), and neither can
  // `activeIndex` when the active segment keeps its position — so the effect
  // would not re-measure and the pill would keep the OLD segment's width. The
  // track's ResizeObserver does not cover it either: under `fullWidth` the
  // segments redistribute inside a track whose own box never changes.
  // The item SET as a primitive dep, via an allocation-free element-wise
  // compare — a `join()` key would build an array and a string on every
  // render just to discover nothing changed.
  const itemsVersion = useItemsVersion(items);
  const activeItem = activeIndex >= 0 ? items[activeIndex] : undefined;
  const activeIconOnly = activeItem
    ? segmentStateOf(activeItem, value, disabled, true).iconOnly
    : false;
  // Whether the pill squares to its own height instead of filling the segment —
  // an icon-only rule that does NOT survive `fullWidth`. See `shouldSquarePill`.
  const squarePill = shouldSquarePill(activeIconOnly, !!fullWidth);
  // An all-icon-only control steps the TRACK radius up to stay concentric
  // with its square cells (Figma icon-only control variants).
  const controlIconOnly = items.every(
    (item) => segmentStateOf(item, value, disabled, true).iconOnly,
  );
  useLayoutEffect(() => {
    const measure = () => {
      const el = activeIndex >= 0 ? segRefs.current[activeIndex] : null;
      if (!el) {
        setPill(null);
        return;
      }
      // Shared with native (`SegmentedControl.progress`) so the icon-only
      // square-pill rule lives in exactly one place.
      const next = pillRectFor(
        {
          x: el.offsetLeft,
          y: el.offsetTop,
          width: el.offsetWidth,
          height: el.offsetHeight,
        },
        squarePill,
      );
      if (!next) {
        setPill(null);
        return;
      }
      setPill((prev) =>
        prev &&
        prev.x === next.x &&
        prev.y === next.y &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next,
      );
    };
    measure();
    const track = segRefs.current[activeIndex]?.parentElement;
    if (!track || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    return () => ro.disconnect();
  }, [activeIndex, squarePill, size, fullWidth, itemCount, itemsVersion]);

  const pillTransition: CSSProperties = disableAnimation
    ? { transition: 'none' }
    : pillTransitionStyle(
        animationDuration ?? DEFAULT_ANIMATION_DURATION_MS,
        animationEasing,
        animationBezier,
      );

  // Per-segment props, hoisted out of the render loop — the web half of the same
  // fix as `SegmentedControl.native.tsx`. Built inline, each segment allocated a
  // fresh `onPress` arrow and `mergedWebProps` object every render, so no segment
  // subtree could bail out. Rebuilt only when the segment set, the selection or a
  // handler actually changes.
  // Latched on shallow equality — see the note on the native file's equivalent.
  const stableSegmentWebProps = useShallowStable(segmentWebProps);

  const segmentProps = useMemo(
    () =>
      items.map((item) => {
        const state = segmentStateOf(item, value, disabled, true);
        return {
          state,
          onPress: () => onValueChange(item.value),
          webProps: {
            'aria-selected': state.isActive,
            ...(stableSegmentWebProps ?? {}),
          },
        };
      }),
    [items, value, disabled, onValueChange, stableSegmentWebProps],
  );

  // One stable ref-setter per index. Written inline, this was a fresh function every
  // render, so React tore down and re-attached EVERY segment ref on each pass (calling
  // the old one with `null` first) — the last reason the segment Pressables could not
  // bail out once their other props were memoised.
  const segmentRefSetters = useMemo(
    () =>
      items.map((_, index) => (el: HTMLElement | null) => {
        segRefs.current[index] = el;
      }),
    [items],
  );

  return (
    <View
      as="nav"
      accessibilityRole="tablist"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      className={cn(
        segmentedControlVariants({ size, iconOnly: controlIconOnly, fullWidth }),
        className,
      )}>
      {pill ? (
        <div
          aria-hidden
          className={cn(
            activeSegmentVariants({ size, activeStyle, iconOnly: activeIconOnly }),
            'left-0 top-0 z-0 motion-reduce:!transition-none',
          )}
          style={{
            transform: `translate3d(${pill.x}px, ${pill.y}px, 0)`,
            width: pill.width,
            height: pill.height,
            ...pillTransition,
          }}
        />
      ) : null}
      {items.map((item, index) => {
        const { state, onPress, webProps: mergedWebProps } = segmentProps[index];

        const segment = (
          <Pressable
            key={item.value}
            ref={segmentRefSetters[index]}
            disabled={state.disabled}
            onPress={onPress}
            accessibilityRole="tab"
            accessibilityLabel={item.accessibilityLabel}
            testID={item.testID ?? subTestID(testID, `option-${item.value}`)}
            className={segmentClassName(
              state,
              size,
              activeStyle,
              fullWidth,
              segmentClassNameProp,
            )}
            webProps={mergedWebProps}
            nativeProps={segmentNativeProps}>
            <SegmentContent
              item={item}
              size={size}
              active={state.isActive}
              activeStyle={activeStyle}
              iconColor="currentColor"
              withKeybind
            />
          </Pressable>
        );
        return item.tooltip?.content ? (
          <Tooltip key={item.value} {...item.tooltip}>
            {segment}
          </Tooltip>
        ) : (
          segment
        );
      })}
    </View>
  );
}
(SegmentedControl as { displayName?: string }).displayName = 'SegmentedControl';
